import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import {
  MagnifyingGlass,
  Plus,
  Dog,
  Cat,
  PawPrint,
  Trash,
} from "@phosphor-icons/react";
import NewPatientDialog from "@/components/NewPatientDialog";
import { toast } from "sonner";
import { speciesLabel as speciesLabelT } from "@/lib/patientUtils";

function speciesBadge(sp) {
  const s = (sp || "").toLowerCase();
  const Icon = s === "cat" ? Cat : s === "dog" ? Dog : PawPrint;
  const color = s === "cat" ? "text-pf-accent" : s === "dog" ? "text-pf-primary" : "text-pf-text-secondary";
  return <Icon size={16} weight="fill" className={color} />;
}

export default function PatientsPage() {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [diagnosisId, setDiagnosisId] = useState("");
  const [diagnoses, setDiagnoses] = useState([]);
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const navigate = useNavigate();

  async function load() {
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (diagnosisId) params.diagnosis_id = diagnosisId;
      const [pats, dxs] = await Promise.all([
        api.get("/patients", { params }),
        api.get("/diagnoses"),
      ]);
      setRows(pats.data);
      setDiagnoses(dxs.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, diagnosisId]);

  async function del(e, p) {
    e.stopPropagation();
    if (!window.confirm(`${t("delete")}: ${p.name}?`)) return;
    try {
      await api.delete(`/patients/${p.id}`);
      toast.success(t("saved"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <h1 className="text-3xl font-display font-extrabold text-pf-text">{t("patients")}</h1>
        <button data-testid="new-patient-btn" onClick={() => setOpenNew(true)} className="pf-btn-primary">
          <Plus size={16} weight="bold" /> {t("newPatient")}
        </button>
      </div>

      <div className="pf-card p-2 flex items-center gap-2 mb-3 flex-wrap sm:flex-nowrap">
        <MagnifyingGlass size={18} className="ml-2 text-pf-text-secondary shrink-0" />
        <input
          data-testid="patients-search-input"
          type="text"
          placeholder={t("searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[180px] bg-transparent outline-none px-1 py-2 text-sm"
        />
        <select
          data-testid="patients-diagnosis-filter"
          value={diagnosisId}
          onChange={(e) => setDiagnosisId(e.target.value)}
          className="pf-input !py-1.5 max-w-[220px]"
        >
          <option value="">{`${t("filterDiagnosis")}: ${t("all")}`}</option>
          {diagnoses.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      <div className="pf-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-pf-bg text-pf-text-secondary">
            <tr>
              <th className="text-left font-semibold px-3 py-3 w-16">ID</th>
              <th className="text-left font-semibold px-3 py-3">{t("name")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden md:table-cell">{t("species")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden md:table-cell">{t("breed")}</th>
              <th className="text-left font-semibold px-3 py-3">{t("owner")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden sm:table-cell">{t("phone")}</th>
              <th className="text-left font-semibold px-3 py-3 hidden lg:table-cell">{t("filterDiagnosis")}</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center text-pf-text-secondary py-10">
                  {t("noResults")}
                </td>
              </tr>
            )}
            {rows.map((p) => (
              <tr
                key={p.id}
                data-testid={`patient-row-${p.id}`}
                className="border-t border-pf-border hover:bg-pf-bg transition-colors cursor-pointer"
                onClick={() => navigate(`/patients/${p.id}`)}
              >
                <td
                  className="px-3 py-3 font-mono text-xs text-pf-text-secondary"
                  data-testid={`patient-id-${p.id}`}
                >
                  #{p.seq ?? "—"}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    {speciesBadge(p.species)}
                    <span className="font-semibold text-pf-text">{p.name}</span>
                  </div>
                </td>
                <td className="px-3 py-3 hidden md:table-cell">{speciesLabelT(p.species, t)}</td>
                <td className="px-3 py-3 hidden md:table-cell">{p.breed || "—"}</td>
                <td className="px-3 py-3">{p.owner?.full_name || "—"}</td>
                <td className="px-3 py-3 hidden sm:table-cell text-pf-text-secondary">{p.owner?.phone || "—"}</td>
                <td className="px-3 py-3 hidden lg:table-cell text-xs text-pf-text-secondary">
                  {p.recent_diagnoses && p.recent_diagnoses.length > 0 ? p.recent_diagnoses.join(", ") : "—"}
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    onClick={(e) => del(e, p)}
                    className="p-1.5 rounded text-pf-text-secondary hover:text-pf-danger hover:bg-pf-danger-bg"
                    data-testid={`patient-delete-${p.id}`}
                    title={t("delete")}
                  >
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewPatientDialog
        open={openNew}
        onOpenChange={setOpenNew}
        onCreated={(p) => {
          setOpenNew(false);
          navigate(`/patients/${p.id}`);
        }}
      />
    </div>
  );
}
